/**
 * useShellSpawn - putting characters from the library onto the roster.
 *
 * - `handleLoadCharacter(character)` opens the spawn dialog for one.
 * - `handleSpawnConfirm(count, magnitude, pointers)` (v7.4.0; `pointers.asStands`
 *   restores a saved condition, v8.412.0): ONE combatant
 *   and two private pointers, so the dialog asks only how many and which
 *   pointers move. One of them already here makes "add" ambiguous, so the
 *   identity question (duplicate / update sheet / replace) is asked; many
 *   of them never is - five Ork Boyz is unambiguously "duplicate".
 *   Spawned combatants join a running order with no prompt (v8.72.0),
 *   keyed by `combatantId` (wrong until v8.70.0 - see the guard in
 *   addTabParticipantBatch).
 * - `loadCharacters(characters)` (v8.23.1): the bulk verb - one each, no
 *   horde sizing, no pointers moved, straight into a running order (v8.72.0).
 *
 * Every arrival comes through the same door (`admitAndRecord`), which names
 * them. The confirm callback is over the length target on purpose: its body
 * is one call, and the rest is a declarative binding list. Extracted from DeathWatchSimulator; the log lines are unchanged.
 */
import { asTheyStood, restoresStanding, standingSummary } from '../engine/roster/standing';
import { useState, useCallback } from 'react';
import { characterToTarget, createTab } from '../engine/factories';
import { findExisting, identityOptions, resolveIdentity } from '../engine/roster/identity';
import { projectAttacker, projectLoadout } from '../engine/roster/projection';
import { infoLine } from './shellCombatant';

/** A new tab for one arrival, named, with a horde's magnitude when one was asked for. */
export function arrivalTab(character, name, id, magnitude = null) {
  const target = characterToTarget(character);
  if (magnitude != null) {
    target.horde.magnitude = magnitude;
    target.horde.maxMagnitude = magnitude;
  }
  target.name = name;
  return createTab(target, id, character);
}

/** Each new tab onto the roster and the wire, one at a time, in order. */
function placeTabs(tabs, character, { setTabSourceCharacter, setTabs, syncTargetToFirestore }) {
  for (const tab of tabs) {
    setTabSourceCharacter(tab.id, character);
    setTabs((prev) => [...prev, tab]);
    syncTargetToFirestore(tab.id, tab.target);
  }
}

/** The fire mode a melee actor's first weapon opens on, or null to leave it. */
export function openingFireMode(firstSlot, stats) {
  if (firstSlot?.weaponType !== 'melee' || !stats) return null;
  if (stats.canLightningAttack) return 'lightning';
  return stats.canSwiftAttack ? 'swift' : null;
}

/** Point the actor at a combatant and pre-select their first weapon (the rack holds only armed ones). */
function useAdoptAsActor({ setActorId, setSelectedSlot, setFireMode }) {
  return useCallback(
    (combatant) => {
      setActorId(combatant.id);
      const { slots } = projectLoadout(combatant);
      if (!slots.length) return;
      setSelectedSlot(0);
      const mode = openingFireMode(slots[0], projectAttacker(combatant, null));
      if (mode) setFireMode(mode);
    },
    [setActorId, setSelectedSlot, setFireMode],
  );
}

/** Bulk load: one each, straight onto the roster and into a running order. */
function loadMany(characters, d) {
  if (!characters?.length) return;
  const stamp = Date.now();
  const { arrivals, count } = d.admitAndRecord(characters);
  const tabs = arrivals.map(({ character, name }, i) => arrivalTab(character, name, `tab-${stamp}-${i}`));
  tabs.forEach((tab, i) => placeTabs([tab], arrivals[i].character, d));
  if (tabs.length) d.pointAtIfUnset(tabs[0].id);
  if (d.turnTracker.trackerState.active && tabs.length) {
    d.turnTracker.addTabParticipantBatch(
      tabs.map((tab, i) => ({ combatantId: tab.id, name: arrivals[i].name, sheet: arrivals[i].character })),
    );
  }
  d.addNarrativeRef.current?.(
    d.activeTab,
    infoLine(`📥 Loaded ${count} character${count === 1 ? '' : 's'} onto the roster.`),
  );
}

/** The bulk-load callback. */
function useLoadCharacters(d) {
  const { activeTab, setTabs, syncTargetToFirestore, setTabSourceCharacter, pointAtIfUnset, admitAndRecord } = d;
  const { turnTracker, addNarrativeRef } = d;
  return useCallback(
    (characters) =>
      loadMany(characters, {
        activeTab,
        setTabs,
        syncTargetToFirestore,
        setTabSourceCharacter,
        pointAtIfUnset,
        admitAndRecord,
        turnTracker,
        addNarrativeRef,
      }),
    [
      activeTab,
      setTabs,
      syncTargetToFirestore,
      setTabSourceCharacter,
      pointAtIfUnset,
      admitAndRecord,
      turnTracker,
      addNarrativeRef,
    ],
  );
}

/** One of them, already here: the identity the user chose, and the pointers they asked for. */
function spawnExisting(character, existing, choice, d) {
  const resolved = resolveIdentity(d.roster, character, choice);
  const combatant = resolved.combatant;
  d.setRoster((prev) =>
    resolved.replaced ? prev.map((c) => (c.id === combatant.id ? combatant : c)) : [...prev, combatant],
  );
  if (d.pointers.actAs) d.adoptAsActor(combatant);
  if (d.pointers.target) d.setActiveTab(combatant.id);
  if (restoresStanding(choice, true)) restoreStanding(combatant.id, character, d);
  // `resolveIdentity` has already decided who this is: only the recording half applies.
  d.record([character]);
  d.addNarrative(
    combatant.id,
    infoLine(
      choice === 'new'
        ? `Added a second "${character.name}" to the roster.`
        : `Updated "${character.name}" from the library.`,
    ),
  );
}

/** Pointers move only if asked. Both ticked is self-targeting - and still one combatant. */
function pointAtFirst(firstId, character, d) {
  if (firstId && d.pointers.target) d.setActiveTab(firstId);
  if (firstId && d.pointers.actAs) {
    d.setActorId(firstId);
    d.setSelectedSlot(character.weapons?.length ? 0 : null);
  }
}

/**
 * "As they stand": the saved condition laid over whoever was just added
 * (v8.412.0). After the add, as its own roster write, so the add itself
 * stays the one door everybody comes through.
 */
function restoreStanding(combatantId, character, d) {
  if (!combatantId || !d.pointers.asStands || !character.standing) return;
  d.setRoster((prev) => prev.map((c) => (c.id === combatantId ? asTheyStood(c, character) : c)));
  d.addNarrative(combatantId, infoLine(`Restored "${character.name}" as they stood (${standingSummary(character)}).`));
}

/**
 * Born hidden (plan map-display, G1): the new ids are hidden BEFORE the tabs
 * are placed - the push never sees them, the order's door refuses them - and
 * nothing is said in the public log, which every player reads.
 */
function bornHidden(tabs, d) {
  if (!d.hide) return d;
  d.reserveHidden(tabs.map((t) => t.id));
  return { ...d, addNarrative: () => {} };
}

/** `count` new arrivals: the third door, through the same one as the other two. */
function spawnNew(character, count, magnitude, spawn) {
  const trackerActive = spawn.turnTracker.trackerState.active;
  const stamp = Date.now();
  // Born hidden: named through the same door, but NOT recorded into the session library, which players read.
  const door = spawn.hide ? spawn.admit : spawn.admitAndRecord;
  const { arrivals } = door(Array.from({ length: count }, () => character));
  const tabs = arrivals.slice(0, count).map((a, i) => arrivalTab(character, a.name, `tab-${stamp}-${i}`, magnitude));
  const d = bornHidden(tabs, spawn);
  placeTabs(tabs, character, d);
  const firstId = tabs[0]?.id ?? null;
  pointAtFirst(firstId, character, d);
  d.addNarrative(firstId || d.activeTab, infoLine(`Added ${count}× "${character.name}" to the roster.`));
  restoreStanding(firstId, character, d);
  if (trackerActive && !d.isLocked('turnTracker') && tabs.length > 0) {
    d.turnTracker.addTabParticipantBatch(
      tabs.map((tab) => ({ combatantId: tab.id, name: tab.target.name, sheet: character })),
    );
  }
}

/** The spawn dialog's answer: ask about an existing one, else add them. */
async function confirmSpawn(character, count, magnitude, spawn) {
  // Born hidden is decided ONCE, here: the GM ticked it and there is somewhere to keep it.
  const d = { ...spawn, hide: !!(spawn.pointers.hidden && spawn.reserveHidden) };
  // A figure born hidden is always a new one: no identity question, which would add it in public.
  const existing = count === 1 && !d.hide ? findExisting(d.roster, character) : null;
  if (!existing) {
    spawnNew(character, count, magnitude, d);
    return;
  }
  const chosen = await d.showDialogAsync('choice', `"${character.name}" is already in the roster:`, {
    choices: identityOptions(existing, character),
  });
  if (chosen) spawnExisting(character, existing, chosen, d);
}

/**
 * @param {object} args - roster, setRoster, setTabs, activeTab,
 *   setActiveTab, setActorId, setSelectedSlot, setFireMode,
 *   setTabSourceCharacter, syncTargetToFirestore, addNarrative,
 *   addNarrativeRef, turnTracker, isLocked, showDialogAsync, record,
 *   admitAndRecord, admit (the door without the library record), pointAtIfUnset,
 *   reserveHidden (the GM's born-hidden reservation, useHiddenFigures).
 * @returns {{ spawnDialog, setSpawnDialog, handleLoadCharacter, loadCharacters, handleSpawnConfirm }}
 */
export default function useShellSpawn(args) {
  const [spawnDialog, setSpawnDialog] = useState(null); // { character } | null
  const handleLoadCharacter = useCallback((character) => setSpawnDialog({ character }), []);
  const adoptAsActor = useAdoptAsActor(args);
  const loadCharacters = useLoadCharacters(args);
  const { roster, setRoster, setTabs, setActiveTab, setActorId, setSelectedSlot, setTabSourceCharacter } = args;
  const { syncTargetToFirestore, addNarrative, activeTab, turnTracker, isLocked, showDialogAsync, record } = args;
  const { admitAndRecord, admit, reserveHidden } = args;
  const handleSpawnConfirm = useCallback(
    async (count, magnitude, pointers = { actAs: false, target: true }) => {
      const character = spawnDialog.character;
      setSpawnDialog(null);
      await confirmSpawn(character, count, magnitude, {
        roster,
        setRoster,
        setTabs,
        setActiveTab,
        setActorId,
        setSelectedSlot,
        setTabSourceCharacter,
        syncTargetToFirestore,
        addNarrative,
        activeTab,
        turnTracker,
        isLocked,
        showDialogAsync,
        record,
        admitAndRecord,
        adoptAsActor,
        pointers,
        reserveHidden,
        admit,
      });
    },
    [
      spawnDialog,
      roster,
      setRoster,
      adoptAsActor,
      setTabs,
      setActiveTab,
      setActorId,
      setSelectedSlot,
      setTabSourceCharacter,
      syncTargetToFirestore,
      addNarrative,
      activeTab,
      turnTracker,
      isLocked,
      showDialogAsync,
      record,
      admitAndRecord,
      admit,
      reserveHidden,
    ],
  );
  return { spawnDialog, setSpawnDialog, handleLoadCharacter, loadCharacters, handleSpawnConfirm };
}
